<script>
  export let title = "The title";

  export let dark = false;

  function getRandomInt(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  const animationDelay = getRandomInt(0, 3);
</script>

<div
  class="title-container {$$props.class}"
  style="--title-animation-delay: {animationDelay}s"
>
  <div
    class="title"
    class:title--dark={dark}
    data-text={title.toLocaleUpperCase()}
  >
    <div class="title-text" class:title-text--dark={dark}>
      {title.toLocaleUpperCase()}
    </div>
  </div>
</div>

<style lang="scss">
  @import "./assets/styles/variables";
  @import "./assets/styles/animations";

  $shadow-color-left-start: $shadow-color-light;
  $shadow-color-left-end: $shadow-color-light;
  $shadow-color-right-start: $shadow-color-dark;
  $shadow-color-right-end: $shadow-color-dark;
  $outer-text-shadow-color: rgba(255, 255, 255, 0.6);
  $outer-text-shadow-offset: 8px;
  $outer-text-shadow-radius: 28px;
  $inner-text-shadow-color: rgba(255, 255, 255, 0.5);
  $inner-text-shadow-offset: 2px;
  $inner-text-shadow-radius: 2px;
  $outer-saturation: 200%;
  $outer-brightness: 150%;
  $outer-blur-s: 0.08em;
  $outer-blur-m: 0.08em;
  $outer-blur-l: 0.04em;
  $inner-saturation: 100%;
  $inner-lighten: 60%;
  $letter-spacing: 4px;
  $font-size-s: 46px;
  $font-size-m: 64px;
  $font-size-l: 96px;
  $line-height-s: 50px;
  $line-height-m: 68px;
  $line-height-l: 108px;

  .title-container {
    display: flex;
    justify-content: center;
    margin: 1rem 0;
    width: 100%;
    text-align: center;
  }

  .title {
    font-family: "DecathlonBeta", Roboto, sans-serif;
    font-weight: bold;
    position: relative;

    &-text {
      animation: flicker 3s infinite step-end var(--title-animation-delay);
      background-image: linear-gradient(
        90deg,
        lighten(
            saturate($background-color-left-start, $inner-saturation),
            $inner-lighten
          )
          0%,
        lighten(
            saturate($background-color-left-end, $inner-saturation),
            $inner-lighten
          )
          100%
      );
      color: transparent;
      background-size: 100%;
      -webkit-background-clip: text;
      -moz-background-clip: text;
      -webkit-background-clip: text;
      -moz-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-text-fill-color: transparent;
      text-fill-color: transparent;
      position: relative;
      -moz-text-stroke-color: transparent;
      -webkit-text-stroke-color: transparent;
      -moz-text-stroke-width: 3px;
      -webkit-text-stroke-width: 3px;
      z-index: 2;
      font-size: $font-size-s;
      line-height: $line-height-s;
      text-transform: uppercase;
      letter-spacing: $letter-spacing;

      &--dark {
        background-image: linear-gradient(
          90deg,
          lighten(
              saturate($background-color-right-start, $inner-saturation),
              $inner-lighten
            )
            0%,
          lighten(
              saturate($background-color-right-end, $inner-saturation),
              $inner-lighten
            )
            100%
        );
      }

      @media (min-width: $breakpoint-small) {
        font-size: $font-size-s;
        line-height: $line-height-s;
        -moz-text-stroke-width: 3px;
        -webkit-text-stroke-width: 3px;
      }

      @media (min-width: $breakpoint-medium) {
        background-image: linear-gradient(
          90deg,
          lighten(
              saturate($background-color-left-start, $inner-saturation),
              $inner-lighten
            )
            0%,
          lighten(
              saturate($background-color-left-end, $inner-saturation),
              $inner-lighten
            )
            50%,
          lighten(
              saturate($background-color-right-start, $inner-saturation),
              $inner-lighten
            )
            50%,
          lighten(
              saturate($background-color-right-end, $inner-saturation),
              $inner-lighten
            )
            100%
        );
        font-size: $font-size-m;
        line-height: $line-height-m;
        -moz-text-stroke-width: 3px;
        -webkit-text-stroke-width: 3px;
      }

      @media (min-width: $breakpoint-large) {
        font-size: $font-size-l;
        line-height: $line-height-l;
        -moz-text-stroke-width: 4px;
        -webkit-text-stroke-width: 4px;
      }
    }

    &:before {
      animation: flicker 3s infinite step-end var(--title-animation-delay);
      font-family: "DecathlonBeta", Roboto, sans-serif;
      font-weight: bold;
      text-transform: uppercase;
      background-image: linear-gradient(
        90deg,
        $shadow-color-left-start 0%,
        $shadow-color-left-end 100%
      );
      content: attr(data-text);
      position: absolute;
      left: 0;
      top: 0;
      z-index: 1;
      -webkit-background-clip: text;
      -moz-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-text-fill-color: transparent;
      text-fill-color: transparent;
      filter: blur($outer-blur-s) saturate($outer-saturation);
      -moz-text-stroke-color: transparent;
      -webkit-text-stroke-color: transparent;
      -moz-text-stroke-width: 2px;
      -webkit-text-stroke-width: 2px;
      font-size: $font-size-s;
      line-height: $line-height-s;
      letter-spacing: $letter-spacing;

      @media (min-width: $breakpoint-small) {
        font-size: $font-size-s;
        line-height: $line-height-s;
        filter: blur($outer-blur-s) saturate($outer-saturation);
        -moz-text-stroke-width: 2px;
        -webkit-text-stroke-width: 2px;
      }

      @media (min-width: $breakpoint-medium) {
        background-image: linear-gradient(
          90deg,
          $shadow-color-left-start 0%,
          $shadow-color-left-end 50%,
          $shadow-color-right-start 50%,
          $shadow-color-right-end 100%
        );
        font-size: $font-size-m;
        line-height: $line-height-m;
        filter: blur($outer-blur-m) saturate($outer-saturation);
        -moz-text-stroke-width: 3px;
        -webkit-text-stroke-width: 3px;
      }

      @media (min-width: $breakpoint-large) {
        font-size: $font-size-l;
        line-height: $line-height-l;
        filter: blur($outer-blur-l) saturate($outer-saturation);
        -moz-text-stroke-width: 4px;
        -webkit-text-stroke-width: 4px;
      }
    }

    &--dark:before {
      background-image: linear-gradient(
        90deg,
        $shadow-color-right-start 0%,
        $shadow-color-right-end 100%
      );
    }

    &:after {
      font-family: "DecathlonBeta", Roboto, sans-serif;
      font-weight: bold;
      text-transform: uppercase;
      background-image: linear-gradient(
        90deg,
        $background-color-left-start -50%,
        $background-color-left-end 150%
      );
      -webkit-background-clip: text;
      -moz-background-clip: text;
      background-clip: text;
      content: attr(data-text);
      position: absolute;
      left: 0;
      top: 0;
      z-index: 3;
      filter: blur(0.02em);
      -webkit-text-fill-color: transparent;
      -moz-text-fill-color: transparent;
      text-fill-color: transparent;
      font-size: $font-size-s;
      line-height: $line-height-s;
      letter-spacing: $letter-spacing;

      @media (min-width: $breakpoint-small) {
        font-size: $font-size-s;
        line-height: $line-height-s;
      }

      @media (min-width: $breakpoint-medium) {
        background-image: linear-gradient(
          90deg,
          $background-color-left-start -50%,
          $background-color-left-end 50%,
          $background-color-right-start 50%,
          $background-color-right-end 150%
        );
        font-size: $font-size-m;
        line-height: $line-height-m;
      }

      @media (min-width: $breakpoint-large) {
        font-size: $font-size-l;
        line-height: $line-height-l;
      }
    }

    &--dark:after {
      background-image: linear-gradient(
        90deg,
        $background-color-right-start -50%,
        $background-color-right-end 150%
      );
    }
  }
</style>
